import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type { VoucherRuleTrigger, VoucherValidityMode } from '@/gql/graphql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { Select } from '@/components/ui/Select';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { formatInteger } from '@/lib/format';
import {
  voucherDiscountLabel,
  voucherTriggerLabel,
  voucherValidityModeLabel,
} from '@/lib/labels';
import { Can } from '@/lib/permissions';
import { useListHref } from '@/hooks/useListLocation';
import { isDefault, optionsOf, useUrlState } from '@/hooks/useUrlState';
import {
  VOUCHER_RULE_TRIGGER_LABELS,
  VOUCHER_RULES_ADVANCED_KEYS,
  voucherRulesUrlSchema,
} from './voucherRulesUrlState';
import { voucherRuleErrorMessage } from './ruleErrors';

const VoucherRulesDoc = graphql(/* GraphQL */ `
  query VoucherRules($includeInactive: Boolean) {
    voucherRules(includeInactive: $includeInactive) {
      id
      name
      label
      trigger
      thresholdPoints
      validityDays
      discountType
      discountPercentage
      discountAmount
      productName
      active
      issuedCount
      scope {
        id
        name
      }
      nightId
      night {
        id
        title
        scope {
          id
          name
        }
      }
      validityMode
      validUntil
    }
  }
`);

const SetVoucherRulesActiveDoc = graphql(/* GraphQL */ `
  mutation SetVoucherRulesActive($ids: [UUID!]!, $active: Boolean!) {
    setVoucherRulesActive(ids: $ids, active: $active) {
      succeeded
    }
  }
`);

interface RuleRow {
  id: string;
  name: string;
  label?: string | null;
  trigger: VoucherRuleTrigger;
  thresholdPoints?: number | null;
  validityDays: number;
  discountType: string;
  active: boolean;
  issuedCount: number;
  /** Ticket claimed only. `otherVenue` is the night's venue when it is not the rule's own. */
  nightTitle?: string | null;
  /** Ticket claimed rule whose night the viewer cannot read. */
  nightHidden?: boolean;
  otherVenue?: string | null;
  validityMode?: VoucherValidityMode | null;
  validUntil?: string | null;
}

export function VoucherRulesListPage() {
  const navigate = useNavigate();
  const { state: url, patch } = useUrlState(voucherRulesUrlSchema);
  const listHref = useListHref();
  const includeInactive = url.show === 'all';
  // The Filters panel opens on mount when a filter inside it is set (a reload, Back, an inbound link).
  const [filtersOpen] = useState(() => VOUCHER_RULES_ADVANCED_KEYS.some((key) => !isDefault(voucherRulesUrlSchema, url, key)));
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);

  const { data, previousData, loading, error, refetch } = useQuery(VoucherRulesDoc, {
    variables: { includeInactive },
    errorPolicy: 'all',
  });
  const viewData = data ?? previousData;
  const [setActive] = useMutation(SetVoucherRulesActiveDoc);

  const rows: RuleRow[] = useMemo(() => {
    const nodes = viewData?.voucherRules ?? [];
    return nodes
      .filter((n) => !url.trigger || n.trigger === url.trigger)
      .map((n) => ({
        id: n.id,
        name: n.name,
        label: n.label,
        trigger: n.trigger,
        thresholdPoints: n.thresholdPoints,
        validityDays: n.validityDays,
        discountType: n.discountType,
        active: n.active,
        issuedCount: n.issuedCount,
        nightTitle: n.night?.title ?? null,
        nightHidden: n.trigger === 'TICKET_CLAIMED' && !!n.nightId && !n.night,
        otherVenue:
          n.night && n.night.scope.id !== n.scope.id ? n.night.scope.name : null,
        validityMode: n.validityMode,
        validUntil: n.validUntil,
      }));
  }, [viewData, url.trigger]);

  const runBulk = async (selected: RuleRow[], nextActive: boolean) => {
    setBulkNotice(null);
    try {
      await setActive({
        variables: { ids: selected.map((r) => r.id), active: nextActive },
      });
    } catch (err) {
      setBulkNotice(voucherRuleErrorMessage(err, 'activate'));
    }
    await refetch();
  };

  const columns = useMemo<ColumnDef<RuleRow>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/voucher-rules/${row.original.id}`}
            state={{ from: listHref }}
            className="font-medium text-gray-900 hover:text-brand dark:text-white"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: 'label',
        header: 'Label',
        cell: ({ row }) =>
          row.original.label ? (
            <span>{row.original.label}</span>
          ) : (
            <span className="text-gray-400 italic dark:text-gray-500">No voucher line</span>
          ),
      },
      {
        id: 'trigger',
        header: 'Mints when',
        cell: ({ row }) => (
          <div>
            <span>
              {voucherTriggerLabel(row.original.trigger)}
              {row.original.trigger === 'POINTS_THRESHOLD' && row.original.thresholdPoints != null
                ? ` (${formatInteger(row.original.thresholdPoints)} pts)`
                : ''}
            </span>
            {row.original.trigger === 'TICKET_CLAIMED' && row.original.nightHidden ? (
              <div
                className="text-xs text-gray-400 italic dark:text-gray-500"
                id={`admin.vouchers.ruleNight.${row.original.id}`}
              >
                Night you cannot view
              </div>
            ) : null}
            {row.original.trigger === 'TICKET_CLAIMED' && row.original.nightTitle ? (
              <div
                className="text-xs text-gray-500 dark:text-gray-400"
                id={`admin.vouchers.ruleNight.${row.original.id}`}
              >
                {row.original.nightTitle}
                {row.original.otherVenue ? ` · ${row.original.otherVenue}` : ''}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'benefit',
        header: 'Benefit',
        cell: ({ row }) => voucherDiscountLabel(row.original.discountType as never),
      },
      {
        id: 'validity',
        header: 'Validity',
        cell: ({ row }) =>
          row.original.trigger === 'TICKET_CLAIMED' && row.original.validityMode
            ? [
                voucherValidityModeLabel(row.original.validityMode),
                row.original.validUntil ? `until ${row.original.validUntil}` : null,
              ]
                .filter(Boolean)
                .join(' ')
            : `${row.original.validityDays} days`,
      },
      {
        id: 'issued',
        header: 'Minted',
        cell: ({ row }) => formatInteger(row.original.issuedCount),
      },
      {
        id: 'active',
        header: 'Active',
        cell: ({ row }) => (
          <Badge tone={row.original.active ? 'success' : 'neutral'}>
            {row.original.active ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
    ],
    [listHref],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Voucher rules
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Scoped to this venue. A rule evaluates the guest&apos;s global points
            balance and mints a voucher here. Redeeming a threshold voucher spends
            those points; birthday, first-card, and one-offs stay free.
          </p>
        </div>
        <Can permission="voucher:manage">
          <Button
            onClick={() => navigate('/voucher-rules/new', { state: { from: listHref } })}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            New rule
          </Button>
        </Can>
      </div>

      {error ? <QueryErrorNotice error={error} title="Could not load voucher rules" /> : null}

      {bulkNotice ? (
        <div
          id="admin.vouchers.ruleBulkNotice"
          role="alert"
          className="flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <div>
            <p className="font-medium">Could not change the selected rules</p>
            <p className="mt-1">{bulkNotice}</p>
          </div>
          <button
            type="button"
            className="text-xs font-medium underline"
            onClick={() => setBulkNotice(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <FilterBar
        defaultOpen={filtersOpen}
        primary={
          <Select
            id="admin.vouchers.ruleTriggerFilter"
            value={url.trigger}
            onChange={(e) => patch({ trigger: e.target.value as VoucherRuleTrigger | '' })}
            options={optionsOf(VOUCHER_RULE_TRIGGER_LABELS, 'Any trigger')}
          />
        }
        advanced={
          <label className="flex items-center gap-2 text-sm">
            <input
              id="admin.vouchers.ruleIncludeInactive"
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => patch({ show: e.target.checked ? 'all' : 'active' })}
              className="h-4 w-4 rounded border-gray-300 text-brand-500"
            />
            Include inactive
          </label>
        }
      />

      <DataGrid<RuleRow>
        data={rows}
        columns={columns}
        loading={loading && !viewData}
        rowId={(row) => row.id}
        selection={{ state: selection, onChange: setSelection }}
        rowMuted={(row) => !row.active}
        actions={[
          {
            id: 'activate',
            label: 'Activate',
            onRun: (selected) => runBulk(selected, true),
          },
          {
            id: 'deactivate',
            label: 'Deactivate',
            variant: 'outline',
            onRun: (selected) => runBulk(selected, false),
          },
        ]}
      />
    </div>
  );
}
